import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#0f172a',
          color: '#f8fafc', fontFamily: 'monospace', padding: '2rem', gap: '1rem',
          zIndex: 9999,
        }}>
          <h1 style={{ fontSize: '1.5rem', color: '#f87171' }}>⚠ Render Error</h1>
          <pre style={{
            background: '#1e293b', padding: '1rem', borderRadius: '8px',
            maxWidth: '90vw', overflowX: 'auto', fontSize: '0.8rem', color: '#fca5a5',
          }}>
            {error.message}
            {error.stack ? '\n\n' + error.stack : ''}
          </pre>
          <button
            onClick={this.reset}
            style={{
              padding: '0.5rem 1.5rem', background: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem',
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
